/**
 * Diagnóstico Profundo de Realtime
 * 
 * Utilitário para rastrear eventos WebSocket, subscrições e invalidações.
 * Totalmente reversível - basta remover as chamadas dos hooks.
 * 
 * Tags de log:
 * [RT][TABLE] - Evento recebido de uma tabela
 * [RT][SUBSCRIBE] - Subscrição registrada
 * [RT][INVALIDATE] - Cache invalidado
 * [RT][WS] - Status do WebSocket
 * [RT][THROTTLE] - Detecção de throttle do browser
 * [RT][MISMATCH] - queryKey não bate com filtro realtime
 */

const DEBUG_ENABLED = true;
const COLORS = {
  TABLE: 'color: #4ade80; font-weight: bold',      // Verde
  SUBSCRIBE: 'color: #60a5fa; font-weight: bold',  // Azul
  INVALIDATE: 'color: #f472b6; font-weight: bold', // Rosa
  WS: 'color: #fbbf24; font-weight: bold',         // Amarelo
  THROTTLE: 'color: #f87171; font-weight: bold',   // Vermelho
  MISMATCH: 'color: #fb923c; font-weight: bold',   // Laranja
};

let lastPingTime = Date.now();
let throttleCheckInterval: NodeJS.Timeout | null = null;

/**
 * Log de evento recebido de uma tabela
 */
export function logEvent(table: string, payload?: unknown) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[RT][TABLE] ${timestamp} ${table}`,
    COLORS.TABLE,
    payload
  );
}

/**
 * Log de subscrição registrada
 */
export function logSubscribe(table: string) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[RT][SUBSCRIBE] ${timestamp} Registrado: ${table}`,
    COLORS.SUBSCRIBE
  );
}

/**
 * Log de invalidação de cache
 */
export function logInvalidation(table: string) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[RT][INVALIDATE] ${timestamp} Cache invalidado: ${table}`,
    COLORS.INVALIDATE
  );
}

/**
 * Log de status do WebSocket
 */
export function logWSStatus(status: string, details?: string) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[RT][WS] ${timestamp} Status: ${status}${details ? ` - ${details}` : ''}`,
    COLORS.WS
  );
}

/**
 * Log de mismatch entre queryKey e filtro realtime
 */
export function logQueryKeyMismatch(table: string, queryKey: unknown[]) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[RT][MISMATCH] ${timestamp} ${table} - queryKey:`,
    COLORS.MISMATCH,
    queryKey
  );
}

/**
 * Detecta throttle do browser comparando timestamps
 * Browsers podem throttle tabs em background
 */
export function detectBrowserThrottle(): boolean {
  const now = Date.now();
  const diff = now - lastPingTime;
  lastPingTime = now;

  // Se diff > 35s (esperado 30s + margem), browser está throttling
  const isThrottled = diff > 35000;

  if (isThrottled && DEBUG_ENABLED) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
    console.log(
      `%c[RT][THROTTLE] ${timestamp} Browser throttle detectado! Diff: ${diff}ms (esperado ~30000ms)`,
      COLORS.THROTTLE
    );
  }

  return isThrottled;
}

/**
 * Inicia detecção periódica de throttle (30s)
 */
export function startThrottleDetection(): () => void {
  if (throttleCheckInterval) {
    clearInterval(throttleCheckInterval);
  }

  lastPingTime = Date.now();
  throttleCheckInterval = setInterval(() => {
    detectBrowserThrottle();
  }, 30000);

  console.log('%c[RT READY] Diagnóstico ativo.', 'color: #22c55e; font-weight: bold; font-size: 14px');

  return () => {
    if (throttleCheckInterval) {
      clearInterval(throttleCheckInterval);
      throttleCheckInterval = null;
    }
  };
}

/**
 * Log genérico para debug
 */
export function logDebug(message: string, data?: unknown) {
  if (!DEBUG_ENABLED) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(`[RT][DEBUG] ${timestamp} ${message}`, data ?? '');
}
